<template>
  <div :class="$style.awPage">
    <component
      :is="pageComponent"
      v-if="pageComponent"
      :key="urlResource.resourceType === 'search' && urlResource.slug"
      v-bind="pageComponentProps"
    />
    <div v-else>
      <aw-spinner color="currentColor" :class="$style.spinner" />
    </div>
  </div>
</template>

<script setup>
  import { computed, defineAsyncComponent, markRaw, nextTick, onMounted, onUnmounted, watch } from 'vue';
  import { createError, navigateTo, useAsyncData, useHead, useNuxtApp, useRoute } from 'nuxt/app';
  import { useAwPerf } from '~~/common/composables/useAwPerf';
  import { isCategoryProductList } from '~~/common/utils';
  import { loadBreadcrumb } from '~~/common/utils/urlResource';
  import { wouldCauseRedirectLoop } from '~~/common/utils/fixDoubleSlash';
  import { useIsReturningEnabled, useIsReturningFromProductPage } from '~~/common/composables/useProductListHistory';
  import { createScrollToProductId } from '~~/common/utils/productListHistoryScroll';
  import { getBoutiqueUrl, getCategoryUrl, getProductUrl, getShortlistUrl } from '~~/common/composables/useUrls';
  import searchPage from '~~/common/components/Page/ProductList/AwProductSearch';
  import { URL_PREFIX } from '~~/common/config/UrlConfig';
  import { CMS_PAGE_MENU_BREADCRUMB_ROOT_TYPE_MAP } from '~~/common/config/CmsPageMenuConfig';
  import { useUrlResourceStore } from '~~/common/stores/urlResource';
  import { useContentsStore } from '~~/common/stores/contents';
  import { useSearchStore } from '~~/common/stores/search';
  import { useCategoryStore } from '~~/common/stores/category';
  import { useProductsStore } from '~~/common/stores/products';
  import { ProductRedirectError, useProductStore } from '~~/common/stores/product';
  import { useDeliveryStore } from '~~/shop/app/stores/delivery';
  import { useBreadcrumbStore } from '~~/common/stores/breadcrumb';
  import { useSeoStore } from '~~/common/stores/seo.js';
  import { PUSH_PRODUCT_VIEW } from '~~/common/plugins/aw-analytics.js';
  import { BOUTIQUE_FLAG_PRODUCTS, BOUTIQUE_REDIRECT_URL, BOUTIQUE_SHORTLIST } from '~~/common/config/BoutiqueConfig';
  import { useLocaleCodeToUrl } from '~~/common/composables/useLanguage';
  import AwSpinner from '~~/common/components/Common/AwSpinner';

  const productPage = defineAsyncComponent(() => import('~/components/Page/Product/Page'));
  const productListPage = defineAsyncComponent(() => import('~~/common/components/Page/ProductList/AwProductListWrapperPage'));
  const categoryListPage = defineAsyncComponent(() => import('~~/common/components/Page/Category/AwCategoryPage'));
  const contentPage = defineAsyncComponent(() => import('~/components/Page/Content/Page'));

  const {
    $awSFMCClient,
    $logger,
    $awt,
    $i18n,
    $router,
    $urlParts,
    $getHost,
  } = useNuxtApp();
  const awPerf = useAwPerf();
  awPerf?.markPhase('awPageSetup');
  const route = useRoute();
  const productStore = useProductStore();
  const productId = computed(() => productStore.data?.id);
  const urlResourceStore = useUrlResourceStore();
  const searchStore = useSearchStore();
  const contentsStore = useContentsStore();
  const seoStore = useSeoStore();
  const categoryStore = useCategoryStore();
  const productsStore = useProductsStore();
  const breadcrumbStore = useBreadcrumbStore();
  const urlResource = computed(() => {
    return urlResourceStore.data;
  });
  const isReturningFromProductPage = useIsReturningFromProductPage();
  const isReturningEnabled = useIsReturningEnabled();
  const isWebshop = computed(() => {
    const langlessPath = $router.currentRoute.value.path.replace(
      new RegExp(
        `^(${$i18n.localeCodes.value.map(lc => '/' + lc).join('|')})($|/)`,
      ),
      '/',
    );
    return langlessPath.startsWith('/shop');
  });
  const asyncKey = computed(() => {
    return `${urlResource.value.resourceType}_${urlResource.value.resourceId}`;
  });
  let searchTimeoutId = null;
  let productViewTimeoutId = null;
  const pageComponentProps = ref(null);
  const pageComponent = ref(false);
  const localeCodeToUrl = useLocaleCodeToUrl();

  onMounted(async () => {
    watch(
      urlResource,
      (newVal) => {
        if (newVal?.resourceType === 'product' && newVal.resourceId) {
          clearTimeout(productViewTimeoutId);
          productViewTimeoutId = setTimeout(() => {
            $awSFMCClient[PUSH_PRODUCT_VIEW]({
              sku: newVal.resourceId,
            });
          }, 1000);
        }
      },
      {
        immediate: true,
      },
    );
    watch(
      () => `${productId.value}_${urlResource.value?.resourceType}`,
      () => {
        if (urlResource.value?.resourceType === 'product' && productId.value) {
          clearTimeout(searchTimeoutId);
          searchTimeoutId = setTimeout(() => {
            searchStore.searchesLog({
              type: 'view',
              resultId: productId.value,
            });
          }, 1000);
        }
      },
      {
        immediate: true,
      },
    );
    watch(
      asyncKey,
      () => {
        updateBreadcrumbs();
      },
    );
    // initClientSide
    const scrollToProductId = createScrollToProductId();

    switch (urlResource.value?.resourceType) {
      case 'category':
        if (!categoryStore.getCategoryFromTree) {
          throw createError({
            statusCode: 404,
            fatal: true,
          });
        } else if (isCategoryProductList(categoryStore.getCategoryFromTree)) {
          if (isReturningFromProductPage.value && isReturningEnabled.value) {
            productsStore.restoreFromProductListHistoryInformation();
          }
          if (isReturningFromProductPage.value) {
            scrollToProductId(productsStore.productListHistoryInformation.productId);
          }
        }
        break;
      case 'boutique': {
                         if (isReturningFromProductPage.value && isReturningEnabled.value) {
                           productsStore.restoreFromProductListHistoryInformation();
                         }

                         if (isReturningFromProductPage.value) {
                           const pIdFromHistory = productsStore.productListHistoryInformation.productId;
                           nextTick(() => {
                             if (isReturningEnabled.value) {
                               const productCard = document.querySelector('.prodPageReturn-' + pIdFromHistory);
                               if (productCard) {
                                 requestAnimationFrame(() => {
                                   productCard.scrollIntoView({
                                     behavior: 'smooth',
                                     block: 'end',
                                   });
                                 });
                               }
                             } else {
                               window.scrollTo(0, 0);
                             }
                           });
                         }
                       }
                       break;
      case 'cms_content':
        setPageComponent(contentPage);
        break;
    }
  });
  onUnmounted(() => {
    clearTimeout(productViewTimeoutId);
    clearTimeout(searchTimeoutId);
  });
  watch(
    () => {
      return useDeliveryStore().setup;
    },
    () => {
      if (urlResource.value?.resourceType === 'boutique' || (urlResource.value?.resourceType === 'category' && isCategoryProductList(categoryStore.getCategoryFromTree))) {
        let id = urlResource.value?.resourceId;
        if (id.includes('|')) {
          id = id.split('|')[1];
        }
        categoryStore.fetchCategoryById(id);
      }
    },
  );
  const {
    data: _nope0,
    error: productError,
  } = await useAsyncData(
    asyncKey.value,
    async () => {
      const err404 = createError({
        statusCode: 404,
        fatal: true,
      });
      try {
        const resourceId = urlResource.value?.resourceId;
        switch (urlResource.value?.resourceType) {
          case 'product': {
                            if (!resourceId) {
                              throw err404;
                            }
                            const product = await productStore.fetchMainProductForProductPage({
                              loc: route.query?.loc,
                              identifierType: 'sku',
                              id: resourceId,
                            });
                            if (!product?.id) {
                              throw err404;
                            }
                          }
                          break;
          case 'category': {
                             categoryStore.setTreeId(Number(resourceId));
                             if (!categoryStore.getCategoryFromTree) {
                               throw err404;
                             } else if (isCategoryProductList(categoryStore.getCategoryFromTree)) {
                               productsStore.setFilterFromQuery(route.query);
                               if (!(isReturningFromProductPage.value && isReturningEnabled.value)) {
                                 await Promise.all([
                                   productsStore.fetchProducts({
                                     isCached: true,
                                     categoryId: resourceId,
                                     itemsPerPage: productsStore.itemsPerPage || 16,
                                     page: 1,
                                     filterParams: productsStore.filterBy,
                                     sortKey: productsStore.sortKey || null,
                                     sortBy: productsStore.sortBy || null,
                                   }),
                                   contentsStore.fetchStaticContentByResourceId({
                                     type: 'product_list_content',
                                     resourceId,
                                   }),
                                 ]);
                               }
                             }
                           }
                           break;
          case 'boutique': {
                             productsStore.setFilterFromQuery(route.query);

                             await Promise.all([
                               categoryStore.fetchCategoryById(resourceId.split('|')[1]),
                               contentsStore.fetchStaticContentByResourceId({
                                 type: 'product_list_content',
                                 resourceId: resourceId.split('|')[1],
                               }),
                             ]);
                             if (!(isReturningFromProductPage.value && isReturningEnabled.value)) {
                               await productsStore.fetchProducts({
                                 isCached: true,
                                 boutiqueId: resourceId.split('|')[1],
                                 itemsPerPage: productsStore.itemsPerPage || 16,
                                 page: 1,
                                 filterParams: productsStore.filterBy,
                                 sortKey: productsStore.sortKey || null,
                                 sortBy: productsStore.sortBy || null,
                               });
                             }
                           }
                           break;
          case 'search': {
                           productsStore.setFilterFromQuery(route.query);
                           productsStore.setSearchText(Array.from(new URLSearchParams(resourceId).values()));
                         }
                         break;
        }
      } catch (err) {
        if (err === err404 || (err instanceof ProductRedirectError)) {
          throw err;
        }
        $logger.error(err);
      }
      return {};
    },
    {
      default: () => ({}),
      // watch: [asyncKey],
    },
  );

  // A CMS page without `contentRootTypes` still belongs under a root level when it
  // sits in one of the sidebar menus, so fall back to the menu it is placed in.
  function getContentRootTypes () {
    const contentData = contentsStore?.data;
    if (contentData?.contentRootTypes?.length) {
      return contentData.contentRootTypes;
    }
    const rootTypeFromMenu = CMS_PAGE_MENU_BREADCRUMB_ROOT_TYPE_MAP[contentData?.inMenu ?? ''];
    return rootTypeFromMenu ? [rootTypeFromMenu] : contentData?.contentRootTypes;
  }

  function updateBreadcrumbs () {
    const breadCrumbs = [
      {
        to: '/',
        disabled: false,
        token: $awt('aw.common.category.home'),
      },
      ...(__AW_IS_SHOP__ && isWebshop.value ? [{
        to: 'shop',
        disabled: false,
        token: $awt('aw.common.category.shop'),
      }] : []),
    ];
    const calculated = loadBreadcrumb({
      urlResource: urlResource.value,
      path: route.path,
      category: categoryStore.getCategoryFromTree,
      productCategories: productStore.data?.categories || [],
      product: {
        token: productStore.data?.selectedVariant?.name,
        to: $router.currentRoute.value.fullPath,
      },
      cmsToken: contentsStore?.data?.title || '',
      urlPrefix: URL_PREFIX,
      contentRootTypes: getContentRootTypes(),
      $awt,
    })?.();
    if (calculated) {
      const breadcrumbs = breadCrumbs.concat(calculated);
      breadcrumbStore.setBreadcrumbs(breadcrumbs);
    }
  }

  updateBreadcrumbs();

  if (productError.value?.statusCode === 404) {
    throw productError.value;
  }
  if (productStore.data?.selectedVariant) {
    switch (urlResource.value?.resourceType) {
      case 'product': {
        const variant = productStore.data?.selectedVariant;
        const slug = urlResource.value.slug;
        if (variant && slug) {
          const correctUrl = getProductUrl(variant);
          const [isRedirectNeeded, redirectionPromise] = redirectIfNeeded(slug, correctUrl);
          if (isRedirectNeeded) {
            await redirectionPromise;
          } else {
            setPageComponent(
              productPage,
              {
                product: useProductStore().data,
              },
            );
          }
        }
        break;
      }
    }
  }

  if (urlResource.value?.resourceType === 'search') {
    setPageComponent(searchPage);
  }

  if (categoryStore.getCategoryFromTree) {
    const slug = urlResource.value?.slug;
    const category = categoryStore.getCategoryFromTree;
    if (slug) {
      let correctUrl;
      if (urlResource.value?.resourceType === 'category') {
        correctUrl = getCategoryUrl(category);
      }
      if (urlResource.value?.resourceType === 'boutique') {
        switch (category.boutiqueContentType) {
          case BOUTIQUE_FLAG_PRODUCTS: {
            correctUrl = getBoutiqueUrl(category);
            break;
          }
          case BOUTIQUE_SHORTLIST: {
            correctUrl = getShortlistUrl({
              identifier: category.boutiqueContentRef,
              shType: 'id',
              boutiqueId: category.id,
              categoryId: category.id,
            });
            break;
          }
          case BOUTIQUE_REDIRECT_URL: {
            const {
              isOuter,
              link,
            } = $urlParts(category.boutiqueContentRef);
            correctUrl = `${isOuter ? '' : localeCodeToUrl.value}${link}`;
            break;
          }
        }
      }
      const [isRedirectNeeded, redirectionPromise] = redirectIfNeeded(slug, correctUrl);
      if (isRedirectNeeded) {
        await redirectionPromise;
      } else {
        if (urlResource.value?.resourceType === 'category') {
          if (isCategoryProductList(categoryStore.getCategoryFromTree)) {
            setPageComponent(
              productListPage,
              {
                category: categoryStore.getCategoryFromTree,
              },
            );
          } else {
            setPageComponent(
              categoryListPage,
              {
                category: categoryStore.getCategoryFromTree,
              },
            );
          }
        } else if (urlResource.value?.resourceType === 'boutique') {
          setPageComponent(
            productListPage,
            {
              category: categoryStore.getCategoryFromTree,
            },
          );
        }
      }
    }
  }

  let title = '';
  const sTitle = seoStore.getSeoReadonlyTitleParts().value;
  if (contentsStore.data?.inCareerMenu || (contentsStore.data?.contentRootTypes || []).includes('career')) {
    title = await seoStore.getCareerPageTitle({
      pageTitle: sTitle.changePostfix('').joinedTitle(),
    });
  }
  if (title) {
    useHead({ title });
  }

  function setPageComponent (newComponent, newProps) {
    if (newProps) {
      pageComponentProps.value = newProps;
    } else {
      pageComponentProps.value = null;
    }
    if (typeof newComponent === 'object') {
      pageComponent.value = markRaw(newComponent);
    } else {
      pageComponent.value = newComponent;
    }
  }

  /**
   * Redirect to the correct URL if the slug does not match the current URL.
   */
  function redirectIfNeeded (slug, correctUrl, redirectCode = 301) {
    if (correctUrl) {
      const separatorChar = slug.match(/#|\?/)?.[0];
      const hashAndQuery = (separatorChar
        ? `${separatorChar}${slug.split(separatorChar).slice(1).join(separatorChar)}`
        : ''
      );
      const isRedirectNeeded = slug !== `${correctUrl}${hashAndQuery}`;
      if (isRedirectNeeded && wouldCauseRedirectLoop($getHost() || '', correctUrl)) {
        $logger.error(`Aborting redirect from "${slug}" to "${correctUrl}": the target contains consecutive slashes, which would cause an infinite redirect loop`);
        throw createError({
          statusCode: 500,
          statusMessage: 'Redirect loop detected',
          fatal: true,
        });
      }
      const promise = (isRedirectNeeded
        ? navigateTo(`${correctUrl}${hashAndQuery}`, { redirectCode: redirectCode })
        : Promise.resolve()
      );
      return [
        isRedirectNeeded,
        promise,
      ];
    } else {
      return [
        false,
        Promise.resolve(),
      ];
    }
  }
</script>

<style module lang="scss" rel="stylesheet/scss">
.awPage {
  @extend %page-min-height;
  margin-top: 0;
}

.spinner {
  color: $color-text-primary;
}
</style>
